import type { QueryClient } from '@tanstack/react-query';

/**
 * Quais consultas ficam velhas depois de uma mutação que mexe em dinheiro.
 *
 * O caixa (`['caixa','atual']`, 30 s) não se refaz sozinho: uma venda em
 * dinheiro, um recebimento ou uma compra paga mudam o esperado, o extrato e as
 * faturas, e a tela que não invalidar a chave certa mostra o número de antes.
 * Cada função devolve a lista de chaves — pura, testável sem navegador — e
 * `invalidar` as aplica.
 */
export type ChaveDeConsulta = readonly string[];

/** O que muda sempre que um lançamento nasce, é realizado ou é estornado. */
export const CHAVES_DO_DINHEIRO: readonly ChaveDeConsulta[] = [
  ['financeiro'],
  ['caixa'],
  ['comandas', 'painel'],
];

/** Fatura e plano: a comanda em Mensalidade e o recebimento mexem nas duas. */
export const CHAVES_DA_FATURA: readonly ChaveDeConsulta[] = [['cobrancas'], ['planos']];

/** A ficha do aluno mostra o consumo pendente e o resumo financeiro. */
export const CHAVES_DO_ALUNO: readonly ChaveDeConsulta[] = [['alunos']];

/** Junta listas de chaves sem repetir nenhuma. */
export function unirChaves(...listas: readonly (readonly ChaveDeConsulta[])[]): ChaveDeConsulta[] {
  const vistas = new Set<string>();
  const unidas: ChaveDeConsulta[] = [];
  for (const chave of listas.flat()) {
    const id = chave.join('\u0000');
    if (vistas.has(id)) continue;
    vistas.add(id);
    unidas.push(chave);
  }
  return unidas;
}

/** Invalida todas, juntas. */
export async function invalidar(
  cliente: QueryClient,
  ...listas: readonly (readonly ChaveDeConsulta[])[]
): Promise<void> {
  await Promise.all(
    unirChaves(...listas).map((queryKey) => cliente.invalidateQueries({ queryKey: [...queryKey] })),
  );
}

/** `['financeiro']`, `['caixa']` e o painel do bar — o dinheiro mudou de lugar. */
export const invalidarDinheiro = (cliente: QueryClient, ...extras: readonly ChaveDeConsulta[]) =>
  invalidar(cliente, CHAVES_DO_DINHEIRO, extras);

/** Dinheiro, fatura e plano: fechar ou estornar uma comanda em Mensalidade. */
export const invalidarDinheiroEFatura = (cliente: QueryClient, ...extras: readonly ChaveDeConsulta[]) =>
  invalidar(cliente, CHAVES_DO_DINHEIRO, CHAVES_DA_FATURA, extras);

/** O que desfazer um fechamento toca: dinheiro, fatura, plano e a ficha do aluno. */
export const invalidarEstornoDeComanda = (cliente: QueryClient) =>
  invalidar(cliente, CHAVES_DO_DINHEIRO, CHAVES_DA_FATURA, CHAVES_DO_ALUNO);
